// ── Landing — V60 풀 리디자인 (토스 스타일 + 당근 오렌지) ─────────────
import React, { useState } from 'react';
import {
  BarChart3, Calculator, Camera, Receipt, Users, ShoppingCart,
  ShieldCheck, ArrowRight, Check, Sparkles,
} from 'lucide-react';
import Login from './Login';
import { IS_CHINA_MODE } from '../chinaMode';   // V144
import { useLang } from '../i18n/LangContext';   // V144
import { TermsOfServiceModal, PrivacyPolicyModal, SecurityPolicyModal } from './LegalModals';

// ── 토큰 (CSS 변수 + 인라인 헬퍼) ─────────────────────────────────────
const C = {
  primary:    '#FF6F0F',          // 당근 오렌지
  primaryDark:'#EA580C',
  primarySoft:'#FAFAFA',          // 카드/아이콘 컨테이너 배경
  primaryFg:  '#9A3412',          // 진한 오렌지 텍스트
  text:       '#18181B',          // gray-900
  textSub:    '#52525B',          // gray-600
  textMuted:  '#A1A1AA',          // gray-400
  border:     '#E4E4E7',          // gray-200
  divider:    '#F4F4F5',          // gray-100
  bg:         '#FFFFFF',
  bgSoft:     '#FAFAFA',          // gray-50
  white:      '#FFFFFF',
};

const SHADOW = {
  sm:  '0 1px 3px rgba(17, 24, 39, 0.06), 0 1px 2px rgba(17, 24, 39, 0.04)',
  md:  '0 4px 8px rgba(17, 24, 39, 0.06), 0 2px 4px rgba(17, 24, 39, 0.04)',
  lg:  '0 10px 20px rgba(17, 24, 39, 0.08), 0 4px 8px rgba(17, 24, 39, 0.04)',
  btn: '0 4px 14px rgba(255, 111, 15, 0.25)',
};

function BrandMark({ size = 28 }) {
  return (
    <img src="/semfit_symbol_t.png" alt="셈핏"
         width={size} height={size}
         style={{ display: 'block', objectFit: 'contain' }} />
  );
}

function Landing() {
  const { lang } = useLang();   // V144
  const [showLogin, setShowLogin] = useState(() => sessionStorage.getItem('ddabak_blocked') === '1');
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);

  // ── 기능 카드 (Lucide 아이콘) ────────────────────────────────────
  const features = [
    { Icon: BarChart3, title: '실시간 매장 대시보드',
      desc: '오늘 매출, 메뉴별 이익, 재고 알림, 목표 달성률을 한눈에. 매일 아침 5분이면 매장 건강도 파악 끝.' },
    { Icon: Calculator, title: '원가 인텔리전스',
      desc: '레시피 등록하면 원가율이 자동 계산. 식재료 시세 + 내 평균 구매가 비교로 가격 협상 무기 확보.' },
    { Icon: Camera, title: '영수증 OCR + 자동 분류',
      desc: '영수증 사진 1장이면 거래·매입·VAT까지 자동 분류. 카드매출 일괄 매칭, 홈택스 엑셀 가져오기.' },
    { Icon: Receipt, title: '세무 어시스턴트',
      desc: '단순/기준/장부 3가지 신고 모드 비교. 의제매입세액공제·부가세 자동 계산. 종소세 신고 30분 가이드.' },
    { Icon: Users, title: '인건비 + 급여명세서',
      desc: '정직원·파트·단기·일용직·프리랜서 5가지 유형 지원. 4대보험·근로소득세·3.3% 자동. 명세서 PDF 출력.' },
    { Icon: ShoppingCart, title: '공동구매 (BETA)',
      desc: '셈핏이 큐레이션한 식자재 공동구매. 시세 대비 -10~25% 절감. 사용자가 모일수록 더 좋은 단가.' },
  ];

  const painPoints = [
    { q: '"이번 달 손익이 어떻게 되지?"', a: '대시보드 1초 — 매출·원가·인건비·순익 자동 계산' },
    { q: '"세무사가 너무 비싸요"', a: '셈핏이 영수증·카드·홈택스 데이터를 다 정리 → 세무사 비용 절반 ↓' },
    { q: '"어떤 메뉴를 빼야할까?"', a: '메뉴별 이익률 + 4분면(Star/Plowhorse/Puzzle/Dog) 자동 분류' },
    { q: '"인건비가 너무 많이 나가요"', a: '직원별 시급/일급 정확 계산 + 4대보험·세금 자동 + 명세서 출력' },
    { q: '"식재료를 더 싸게 살 수는 없을까?"', a: '시세 대비 내 평균가 비교 + 공동구매로 -10~25% 절감' },
    { q: '"부가세 신고가 막막해요"', a: '의제매입·과세/면세 자동 분리 + 단순/기준/장부 3모드 비교' },
  ];

  // V144: 중국 모드(jjggfood.com)·셈핏 안드로이드 앱은 한국어 소개 페이지 없이 바로 로그인 화면
  //       (중국 직원에게 주소를 보내면 한국어 소개 페이지부터 나왔다)
  const directLogin = IS_CHINA_MODE || (typeof navigator !== 'undefined' && /SemfitApp\//.test(navigator.userAgent || ''));
  if (directLogin) return <Login />;

  // ── 로그인 모달 화면 ─────────────────────────────────────────────
  if (showLogin) {
    return (
      <div style={{ position: 'relative' }}>
        <button onClick={() => setShowLogin(false)}
          style={{
            position: 'fixed', top: 20, left: 20, zIndex: 1000,
            padding: '10px 18px', borderRadius: 999,
            border: `1.5px solid ${C.border}`, background: C.white,
            color: C.text, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            boxShadow: SHADOW.sm, transition: 'all 180ms',
          }}>
          {lang === 'zh' ? '← 返回' : '← 돌아가기'}
        </button>
        <Login />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text }}>

      {/* ── 헤더 ──────────────────────────────────────────────── */}
      <header style={{
        padding: '0 40px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        position: 'sticky', top: 0, zIndex: 100,
        borderBottom: `1px solid ${C.divider}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BrandMark size={32} />
          <span style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: '-0.02em' }}>셈핏</span>
        </div>
        <button onClick={() => setShowLogin(true)}
          style={{
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: C.text, color: C.white, fontSize: 14, fontWeight: 700,
            cursor: 'pointer', transition: 'all 180ms',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#000'}
          onMouseLeave={e => e.currentTarget.style.background = C.text}>
          시작하기
        </button>
      </header>

      {/* ── 히어로 ──────────────────────────────────────────────── */}
      <section style={{ padding: '90px 40px 120px', background: C.bg }}>
        <div style={{ maxWidth: 880, margin: '0 auto', textAlign: 'center' }}>
          {/* 베타 배지 */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 999,
            background: C.primarySoft, color: C.primaryFg,
            fontSize: 13, fontWeight: 700, marginBottom: 28,
            border: `1px solid #F4F4F5`,
          }}>
            <Sparkles size={14} strokeWidth={2.5} />
            평생 이용권 출시 — 월 이용료 없이 한 번 결제로 평생
          </div>

          {/* 헤드라인 — V79: 충격 톤 (비교 가능한 숫자) */}
          <h1 style={{
            fontSize: 'clamp(34px, 5.8vw, 60px)', fontWeight: 800,
            color: C.text, lineHeight: 1.18, marginBottom: 22,
            letterSpacing: '-0.03em', margin: '0 0 22px',
          }}>
            이익률 8%,<br />
            같은 업종 평균은 <span style={{ color: C.primary }}>14%.</span>
          </h1>

          <p style={{
            fontSize: 18, color: C.textSub, marginBottom: 14,
            lineHeight: 1.6, maxWidth: 620, margin: '0 auto 14px',
            fontWeight: 500,
          }}>
            6%의 차이는 운이 아니라, 어디선가 새고 있다는 신호입니다.<br />
            셈핏이 어디서 +가 났고 어디서 -가 났는지 정확히 보여드립니다.
          </p>

          <p style={{
            fontSize: 13, color: C.textMuted, marginBottom: 38,
            lineHeight: 1.5, maxWidth: 560, margin: '0 auto 38px',
            fontWeight: 500, letterSpacing: '0.01em',
          }}>
            측정하지 않은 것은 개선할 수 없습니다.
          </p>

          {/* CTA 버튼 — V65: 그림자 제거 */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowLogin(true)}
              style={{
                padding: '16px 32px', borderRadius: 12, border: 'none',
                background: C.primary, color: C.white, fontSize: 16,
                fontWeight: 700, cursor: 'pointer',
                transition: 'all 180ms',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = C.primaryDark; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.transform = ''; }}>
              3일 무료 체험 시작하기 <ArrowRight size={18} strokeWidth={2.5} />
            </button>
            <a href="#features"
              style={{
                padding: '16px 32px', borderRadius: 12,
                border: `1.5px solid ${C.border}`, background: C.white,
                color: C.text, fontSize: 16, fontWeight: 700,
                cursor: 'pointer', textDecoration: 'none',
                display: 'inline-flex', alignItems: 'center',
                transition: 'all 180ms',
              }}
              onMouseEnter={e => e.currentTarget.style.background = C.bgSoft}
              onMouseLeave={e => e.currentTarget.style.background = C.white}>
              기능 살펴보기
            </a>
          </div>

          {/* 미리보기 카드 4개 */}
          <div style={{
            marginTop: 64, display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12, maxWidth: 760, margin: '64px auto 0',
          }}>
            {[
              { label: '이번 달 매출', value: '12,400,000원' },
              { label: '평균 원가율', value: '34.2%' },
              { label: '인건비 비율', value: '21.8%' },
              { label: 'VAT 납부 예정', value: '283,000원' },
            ].map((card, i) => (
              <div key={i} style={{
                background: C.white,
                borderRadius: 14, padding: '16px 18px',
                border: `1px solid ${C.divider}`,
                textAlign: 'left',
              }}>
                <div style={{ fontSize: 12, color: C.textSub, marginBottom: 6, fontWeight: 500 }}>
                  {card.label}
                </div>
                <div style={{ fontSize: 19, fontWeight: 800, color: C.text, letterSpacing: '-0.02em' }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 페인포인트 매칭 ──────────────────────────────────────── */}
      <section style={{ padding: '90px 40px', background: C.bgSoft }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, textAlign: 'center',
            marginBottom: 48, color: C.text, letterSpacing: '-0.02em', lineHeight: 1.25,
          }}>
            왜 외식업 사장님께<br />셈핏이 필요한가요?
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 14,
          }}>
            {painPoints.map((item, i) => (
              <div key={i} style={{
                background: C.white, borderRadius: 16,
                padding: '22px 24px',
                border: `1px solid ${C.divider}`,
                transition: 'all 180ms',
              }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 10, letterSpacing: '-0.01em' }}>
                  {item.q}
                </div>
                <div style={{ fontSize: 14, color: C.textSub, lineHeight: 1.65 }}>
                  → {item.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 기능 카드 그리드 ───────────────────────────────────── */}
      <section id="features" style={{ padding: '100px 40px', background: C.bg }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, textAlign: 'center',
            marginBottom: 12, color: C.text, letterSpacing: '-0.02em', lineHeight: 1.2,
          }}>
            꼭 필요한 기능, 다 있어요
          </h2>
          <p style={{ textAlign: 'center', color: C.textSub, marginBottom: 56, fontSize: 17, fontWeight: 500 }}>
            엑셀 + 가계부 + 세무사 일을 셈핏 하나로
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 18,
          }}>
            {features.map((f, i) => (
              <div key={i} style={{
                padding: 28, borderRadius: 20,
                background: C.white,
                border: `1px solid ${C.divider}`,
                transition: 'border-color 200ms',
                cursor: 'default',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.border; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.divider; }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: C.bgSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 18, color: C.text,
                }}>
                  <f.Icon size={22} strokeWidth={1.75} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 10, color: C.text, letterSpacing: '-0.01em' }}>
                  {f.title}
                </h3>
                <p style={{ fontSize: 14, color: C.textSub, lineHeight: 1.7, margin: 0 }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 보안 & 신뢰 ──────────────────────────────────────── */}
      <section style={{ padding: '70px 40px', background: C.bgSoft }}>
        <div style={{ maxWidth: 880, margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 52, height: 52, borderRadius: 12,
            background: C.bg, border: `1px solid ${C.divider}`,
            color: C.text, marginBottom: 20,
          }}>
            <ShieldCheck size={24} strokeWidth={1.75} />
          </div>
          <h2 style={{ fontSize: 26, fontWeight: 800, marginBottom: 12, color: C.text, letterSpacing: '-0.02em' }}>
            내 매장 데이터, 안전하게 지킵니다
          </h2>
          <p style={{ color: C.textSub, marginBottom: 28, fontSize: 15, lineHeight: 1.7, maxWidth: 600, margin: '0 auto 28px' }}>
            개인정보보호법 25조·38조 준수 · 사용자별 데이터 격리 · 회원탈퇴 시 데이터 즉시 삭제
          </p>
          <div style={{
            display: 'flex', gap: 8, justifyContent: 'center',
            flexWrap: 'wrap', fontSize: 13, color: C.text,
          }}>
            {['Firebase Auth + App Check', 'reCAPTCHA Enterprise', '탈퇴 시 전 데이터 삭제'].map(label => (
              <span key={label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', background: C.white,
                borderRadius: 999, border: `1px solid ${C.border}`,
                fontWeight: 600,
              }}>
                <Check size={14} strokeWidth={3} color={C.primary} />
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3단계로 시작 ─────────────────────────────────────── */}
      <section style={{ padding: '90px 40px', background: C.bg }}>
        <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(28px, 4vw, 38px)', fontWeight: 800, marginBottom: 56,
            color: C.text, letterSpacing: '-0.02em',
          }}>
            3단계로 시작
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              '구글로 1초 가입 — 3일 전 기능 무료 체험 (신용카드 불필요)',
              '거래 입력 또는 영수증 OCR로 데이터 쌓기',
              '대시보드에서 매장 현황 파악 → 의사결정',
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 20,
                background: C.white, borderRadius: 16, padding: '20px 26px',
                border: `1px solid ${C.divider}`,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: C.bgSoft, color: C.text,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 700, flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 16, fontWeight: 600, color: C.text, textAlign: 'left' }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 베타 CTA — V65: 다크 그라데이션 제거, 라이트 톤 ── */}
      <section style={{
        padding: '100px 40px', textAlign: 'center',
        background: C.bgSoft,
        color: C.text,
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(30px, 4.5vw, 44px)', fontWeight: 800, marginBottom: 16, letterSpacing: '-0.02em', color: C.text }}>
            구독 없이, 한 번 결제로 평생
          </h2>
          <p style={{ color: C.textSub, fontSize: 16, marginBottom: 8, lineHeight: 1.7, fontWeight: 500 }}>
            평생 이용권 런칭 특가 39,000원 (30명 한정, 이후 59,000원) — 월 이용료 없음, 업데이트 평생 무료
          </p>
          <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 36 }}>
            3일간 전 기능을 무료로 써보고, 마음에 들면 평생 소장하세요
          </p>
          <button onClick={() => setShowLogin(true)}
            style={{
              padding: '16px 40px', borderRadius: 12, border: 'none',
              background: C.primary, color: C.white, fontSize: 16,
              fontWeight: 700, cursor: 'pointer',
              transition: 'all 180ms',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = C.primaryDark; }}
            onMouseLeave={e => { e.currentTarget.style.background = C.primary; }}>
            3일 무료 체험 시작하기 <ArrowRight size={18} strokeWidth={2.5} />
          </button>
          <p style={{ color: C.textMuted, fontSize: 13, marginTop: 20 }}>
            구글 또는 이메일로 1초 가입 · 신용카드 불필요 · 언제든 탈퇴 가능
          </p>
        </div>
      </section>

      {/* ── 푸터 ────────────────────────────────────────────── */}
      <footer style={{
        padding: '40px 40px 32px', textAlign: 'center',
        background: C.bgSoft, color: C.textSub, fontSize: 13, lineHeight: 1.8,
        borderTop: `1px solid ${C.divider}`,
      }}>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <BrandMark size={24} />
          <span style={{ fontWeight: 800, color: C.text, fontSize: 16, letterSpacing: '-0.02em' }}>셈핏</span>
        </div>
        <div style={{ color: C.text, fontWeight: 600, marginBottom: 6 }}>
          © 2026 셈핏 · 외식업 사장님을 위한 운영 OS
        </div>
        <div style={{ fontSize: 12, color: C.textMuted }}>
          사업자등록번호 263-19-02810 · 대표 장우 · 경상남도 남해군 삼동면 봉화로 17 · 문의 u012me@gmail.com
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
          <button onClick={() => setTermsOpen(true)}
            style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 12 }}>
            이용약관
          </button>
          <button onClick={() => setPrivacyOpen(true)}
            style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 12 }}>
            개인정보처리방침
          </button>
          <button onClick={() => setSecurityOpen(true)}
            style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 12 }}>
            보안 정책
          </button>
        </div>
      </footer>

      <TermsOfServiceModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} />
      <PrivacyPolicyModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <SecurityPolicyModal isOpen={securityOpen} onClose={() => setSecurityOpen(false)} />
    </div>
  );
}

export default Landing;
